import { ColumnDef, Row } from "@tanstack/react-table";
import { useState } from "react";
import Pagination from "@/components/atoms/Pagination";
import History from "@/types/History";
import {
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/molecules/Table";
import SearchBox from "@/components/molecules/SearchBox";
import DataTable from "@/components/organisms/DataTable";
import { capitalize } from "@/lib/utils";

type Props = {
  histories: History[];
};

const HistoryTab = ({ histories }: Props) => {
  const [pagination, setPagination] = useState({ page: 1, per_page: 10 });
  const [totalCount, setTotalCount] = useState<number>(histories.length);
  const [totalPages, setTotalPages] = useState<number>(Math.ceil(totalCount / pagination.per_page));
  const [filteredHistories, setFilteredHistories] = useState<History[]>(histories);
  const [search, setSearch] = useState<string | null>(null);

  const columns: ColumnDef<History>[] = [
    {
      accessorKey: "expender",
      header: () => null,
      cell: ({ row }) =>
        row.original.tracked_changes? (
          <div className="cursor-pointer" onClick={() => row.toggleExpanded()}>
            {row.getCanExpand() &&
              (row.getIsExpanded() ? <ChevronDown /> : <ChevronRight />)}
          </div>
        ) : null,
      size: 10,
      minSize: 10,
    },
    {
      accessorKey: "updateType",
      header: "Action",
      cell: ({ row }) => {
        const { relation_type, relation_id, action_type } = row.original;
        return relation_type ? `${capitalize(relation_type)} with id ${relation_id} was ${capitalize(action_type)}` : `Invoice Itself ${capitalize(action_type)}`;
      },
    },
    {
      accessorKey: "user.name",
      header: "Actioned By",
    },
    {
      accessorKey: "created_at",
      header: "Action Time",
      cell: ({ row }) => {
        const date = new Date(row.original.created_at);
        const formattedDate = date.toLocaleDateString('en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
        const formattedTime = date.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
        return `${formattedDate} ${formattedTime}`;
      },
    },
  ];

  const formatNumber = (num: number) => {
    if (num === 0) return '';
    return `${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num)}`;
  };

  const formatServicePeriod = (period: string) => {
    const year = period.slice(0, 4);
    const month = period.slice(4, 6);
    const date = new Date(`${year}-${month}-01`);
    return date.toLocaleString('en-US', { month: 'short', year: 'numeric' }).replace(' ', '/');
  };

  const renderSubComponent = ({ row }: { row: Row<History> }) => {
    const { tracked_changes, action_type } = row.original;
  
    const renderSowAmountsChanges = (oldAmounts: any[], newAmounts: any[]) => {
      const changes: JSX.Element[] = [];
      newAmounts.forEach((newAmount, i) => {
        const oldAmount = (oldAmounts || []).filter((oldAmount) => oldAmount.sow_id === newAmount.sow_id && oldAmount.period === newAmount.period)[0]
        
        changes.push(
          <TableRow key={`${newAmount.sow_id}-amount-${i}`}>
            {action_type === "updated" && oldAmount.amount != newAmount.amount && (
              <>
                <TableCell className="w-1/4 px-4">{newAmount.sow_id} - {formatServicePeriod(newAmount.period)}</TableCell>
                <TableCell className="w-1/4 px-4">
                  {`Amount: ${formatNumber(oldAmount.amount)}`}
                </TableCell>
                <TableCell className="w-1/4 px-4">
                  {`Amount: ${formatNumber(newAmount.amount)}`}
                </TableCell>
              </>
            )}
            {action_type === "created" && (
              <>
                <TableCell className="w-1/4 px-4">{newAmount.sow_id} - {formatServicePeriod(newAmount.period)}</TableCell>
                <TableCell className="w-1/4 px-4">
                  {`Amount: ${formatNumber(newAmount.amount)}`}
                </TableCell>
              </>
                
            )}
          </TableRow>
        );
      });

      return changes;
    };
  
    return (
      <Table className="w-full bg-gray-100 dark:bg-background">
        <TableHeader className="bg-gray-50 dark:bg-gray-900">
          <TableRow>
            <TableHead className="w-1/4 px-4 font-bold">Property</TableHead>
            {action_type === "updated" ? (
              <>
                <TableHead className="w-1/4 px-4 font-bold">From</TableHead>
                <TableHead className="w-1/4 px-4 font-bold">To</TableHead>
              </>
            ) : (
              <TableHead className="w-1/4 px-4 font-bold">Value</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {tracked_changes &&
            Object.entries(tracked_changes).map(([field, value], index) => {
              if (field === "sow_amounts") {
              
                const [oldValue = [], newValue = []] = value;
                return renderSowAmountsChanges(oldValue, newValue);
              } else if (action_type === "updated") {
                const [oldValue, newValue] = value as [string, string];
                return (
                  <TableRow key={`${row.id}-sub-${index}`}>
                    <TableCell className="w-1/4 px-4">{field}</TableCell>
                    <TableCell className="w-1/4 px-4">{oldValue}</TableCell>
                    <TableCell className="w-1/4 px-4">{newValue}</TableCell>
                  </TableRow>
                );
              } else if (action_type === "created") {
                const [, newValue] = value as [null, string]; // Old value is always null for created
                return (
                  <TableRow key={`${row.id}-sub-${index}`}>
                    <TableCell className="w-1/4 px-4">{field}</TableCell>
                    <TableCell className="w-1/4 px-4">{newValue}</TableCell>
                  </TableRow>
                );
              } else if (action_type === "deleted") {
                const oldValue = value as unknown as string; // Only old value exists for deleted
                return (
                  <TableRow key={`${row.id}-sub-${index}`}>
                    <TableCell className="w-1/4 px-4">{field}</TableCell>
                    <TableCell className="w-1/4 px-4">{oldValue}</TableCell>
                  </TableRow>
                );
              }
            })}
        </TableBody>
      </Table>
    );
  };
  

  const handleSearch = (search: string) => {
    setSearch(search);
    const filteredData = histories.filter((history) =>
      !search
        ? true
        : history.user.name.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredHistories(filteredData)
    setTotalCount(filteredData.length);
    setTotalPages(Math.ceil(filteredData.length / pagination.per_page));
  };

  const handlePageChange = (page: number) => { 
    setPagination(prev => ({ ...prev, page }));
  };

  return (
    <div>
      <div className="flex items-center justify-between pt-6 pb-4 m-4">
        <SearchBox onSearch={handleSearch} />
      </div>
   
      <DataTable
        columns={columns}
        data={filteredHistories}
        currentPage={pagination.page}
        pageSize={pagination.per_page}
        isDifferentTableColumns= {true}
        renderSubComponent={renderSubComponent}
      />
      <Pagination
        currentPage={pagination.page}
        totalPages={totalPages}
        totalCount={totalCount}
        perPage={pagination.per_page}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default HistoryTab;
